// Office shell extras (design_handoff_round2 §4A, kept in the rail by the user's choice):
// action counts on rail items, the sync chip, and one search box for everything: any work order,
// estimate, customer, boat or mooring, any page ("fuel", "dry stack"), and "new …" to create one.
// Records come from what the app already keeps on the device (IndexedDB), so it works offline and
// costs no reads. With the box empty it offers quick create and your recent picks.

import { ref, get, onValue, query, orderByChild, equalTo } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, database } from './initFirebase';
import { idbCursorAll, escapeHTML } from './utilities';
import { mountSyncChip } from './syncChip';
import { HUBS, hubOf, memberTab, isHub } from './railHubs';
import { watchWaiting } from './threadData';

let hooks = {};

/* ============ COUNTS ============ */

function setCount(id, n, title) {
  const el = document.getElementById(id);
  if (!el) return;
  el.hidden = !n;
  el.textContent = n > 99 ? '99+' : String(n || '');
  if (title) el.title = title;
}

// Listeners need a signed-in user (a listener refused before sign-in never retries),
// so they start on the first sign-in.
let countsWired = false;
// Email account health (design_handoff_email E): admin and office see "Email needs reconnecting"
// beside the Live chip while the company sending account (Settings › Email) has to be signed in again.
function watchMailHealth() {
  const uid = auth.currentUser?.uid;
  if (!uid) return;
  get(ref(database, `users/${uid}/role`)).then((s) => {
    if (!/admin|office/i.test(String(s.val() || ''))) return;
    const host = document.getElementById('railMailHealth');
    if (!host) return;
    // A card at the top of the user menu, and an amber dot on the avatar so it's seen with the menu closed.
    host.innerHTML = `<button type="button" class="rail-mailhealth-btn" role="menuitem"><span class="rail-mailhealth-dot"></span>
      <span class="rail-mailhealth-text"><b>Email needs reconnecting</b><span>Open Settings › Email</span></span></button>`;
    host.querySelector('button').addEventListener('click', () => {
      if (hooks.openSettings) hooks.openSettings('email');
      else window.open('/settings?tab=email', '_blank', 'noopener');
    });
    const warn = document.getElementById('railUserWarn');
    onValue(ref(database, 'emailAccount/status'), (snap) => {
      const on = snap.val() === 'reconnect';
      host.hidden = !on;
      if (warn) warn.hidden = !on;
      document.getElementById('user-data')?.setAttribute('aria-label', `${document.getElementById('currentUserName')?.textContent || 'You'}${on ? ' — email needs reconnecting' : ''}`);
    }, () => {});
  }).catch(() => {});
}

function wireCounts() {
  onAuthStateChanged(auth, (user) => {
    if (!user || countsWired) return;
    countsWired = true;
    startCounts();
  });
}

function startCounts() {
  onValue(ref(database, 'launchDispatch/queue'), (snap) => {
    const n = Object.keys(snap.val() || {}).length;
    setCount('rail-count-dispatch', n, `${n} waiting for the launch`);
  }, () => {});
  // Portal conversations with a customer message newer than the office last read.
  let threads = {}, officeRead = {}, signups = 0;
  const paintMessages = () => {
    const n = Object.entries(threads).filter(([cid, t]) => Object.values(t || {}).some(m => m && m.From === 'customer' && (m.SentAt || 0) > (officeRead[cid] || 0))).length;
    setCount('rail-count-messages', n + signups, `${n} conversation${n === 1 ? '' : 's'} with something new${signups ? `, ${signups} portal sign-up${signups === 1 ? '' : 's'} waiting` : ''}`);
  };
  // Portal sign-ups waiting for the office (7.3); admin/office only.
  onValue(query(ref(database, 'portalSignups'), orderByChild('status'), equalTo('waiting')), snap => { signups = Object.keys(snap.val() || {}).length; paintMessages(); }, () => {});
  onValue(ref(database, 'customerPortal/threads'), (snap) => { threads = snap.val() || {}; paintMessages(); }, () => {});
  onValue(ref(database, 'customerPortal/officeRead'), (snap) => { officeRead = snap.val() || {}; paintMessages(); }, () => {});
  // Service requests (7.1) not yet looked at; only admin/office can read them (others: no count).
  onValue(query(ref(database, 'serviceRequests'), orderByChild('status'), equalTo('new')), (snap) => {
    const n = Object.keys(snap.val() || {}).length;
    setCount('rail-count-requests', n, `${n} new service request${n === 1 ? '' : 's'}`);
  }, () => {});
  // Techs' questions waiting on the office (threadData.js; only the threads' small summaries).
  watchWaiting((n) => setCount('rail-count-techq', n, `${n} tech question${n === 1 ? '' : 's'} waiting on the office`));
  watchMailHealth();
  onValue(ref(database, 'launchDispatch/pendingPositions'), (snap) => {
    const n = Object.keys(snap.val() || {}).length;
    setCount('rail-count-field', n, `${n} mooring position${n === 1 ? '' : 's'} to review`);
  }, () => {});
}

/* ============ USER CHIP ============ */

function wireAvatar() {
  const name = document.getElementById('currentUserName');
  const av = document.getElementById('railUserAvatar');
  if (!name || !av) return;
  const paint = () => {
    const parts = (name.textContent || '').trim().split(/\s+/).filter(Boolean);
    av.textContent = parts.length ? (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() : '';
  };
  new MutationObserver(paint).observe(name, { childList: true, characterData: true, subtree: true });
  paint();
}

/** On a phone the rail is a slide-out menu: close it once something in it has been chosen. */
function closeMobileRail() {
  const mb = document.getElementById('mobile-menu-button');
  const rail = document.querySelector('.page-select');
  if (mb && rail && mb.offsetParent !== null && !rail.classList.contains('hide-mobile')) {
    mb.click();
    mb.querySelector('svg')?.classList.toggle('toggle');
  }
}

/* ============ USER MENU ============ */
// The user chip opens one menu (design_handoff_rail_fold §2.4): the mail warning, Settings, Help,
// Customize menu, the sync chip (still tappable for what's waiting) and Sign out. Upward from the
// chip; beside the slim icon rail; a bottom sheet on a phone.

const menuEl = () => document.getElementById('railUserMenu');
const chipEl = () => document.getElementById('user-data');
const rows = () => [...menuEl().querySelectorAll('[role="menuitem"]')].filter(b => b.offsetParent !== null);

function openUserMenu() {
  const menu = menuEl();
  if (!menu || !menu.hidden) return;
  menu.hidden = false;
  document.getElementById('rail-user-scrim').hidden = !window.matchMedia('(max-width: 768px)').matches;
  chipEl().setAttribute('aria-expanded', 'true');
  chipEl().classList.add('open');
  rows()[0]?.focus();
}

export function closeUserMenu({ focusChip = false } = {}) {
  const menu = menuEl();
  if (!menu || menu.hidden) return;
  menu.hidden = true;
  document.getElementById('rail-user-scrim').hidden = true;
  chipEl().setAttribute('aria-expanded', 'false');
  chipEl().classList.remove('open');
  if (focusChip) chipEl().focus();
}

function wireUserMenu() {
  const chip = chipEl();
  const menu = menuEl();
  if (!chip || !menu) return;
  chip.addEventListener('click', (e) => {
    e.stopPropagation();
    if (menu.hidden) openUserMenu(); else closeUserMenu();
  });
  // Choosing a row closes the menu (its own handler has already run). On a phone the slide-out
  // rail closes too, as a rail link does.
  menu.addEventListener('click', (e) => {
    if (!e.target.closest('[role="menuitem"]')) return;
    closeUserMenu();
    closeMobileRail();
  });
  menu.addEventListener('keydown', (e) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
    const list = rows();
    if (!list.length) return;
    e.preventDefault();
    const i = list.indexOf(document.activeElement);
    const j = e.key === 'Home' ? 0 : e.key === 'End' ? list.length - 1
      : e.key === 'ArrowDown' ? (i + 1) % list.length : (i - 1 + list.length) % list.length;
    list[j].focus();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !menu.hidden) closeUserMenu({ focusChip: true });
  });
  document.addEventListener('click', (e) => {
    if (!menu.hidden && !menu.contains(e.target) && !chip.contains(e.target)) closeUserMenu();
  });
  document.getElementById('rail-user-scrim')?.addEventListener('click', () => closeUserMenu({ focusChip: true }));
}

/** The chip's line under the name mirrors the sync chip in the menu: dot colour and words. */
function wireUserSync() {
  const host = document.getElementById('railSync');
  const line = document.getElementById('railUserSync');
  if (!host || !line) return;
  const paint = () => {
    const chip = host.querySelector('.sync-chip');
    if (!chip) return;
    const key = (chip.className.match(/sync-(live|saving|offline|failed)/) || [])[1] || 'live';
    const label = chip.querySelector('.sync-label')?.textContent || 'Live';
    line.dataset.state = key;
    line.querySelector('.rail-user-sync-label').textContent = label;
  };
  new MutationObserver(paint).observe(host, { subtree: true, attributes: true, attributeFilter: ['class'], childList: true, characterData: true });
  paint();
}

/* ============ SEARCH ============ */

let index = null;
let indexAt = 0;
const INDEX_TTL = 2 * 60 * 1000;

const norm = s => String(s || '').toLowerCase();
const custName = c => [c.First_Name, c.Last_Name].filter(Boolean).join(' ') || c.Company || c.Customer_Name || '';
const custSort = c => [c.Last_Name, c.First_Name].filter(Boolean).join(', ') || custName(c);

async function buildIndex() {
  const [customers, jobs, tags, estimates, parts, vendors] = await Promise.all([
    idbCursorAll('customerData').catch(() => []),
    idbCursorAll('jobData').catch(() => []),
    idbCursorAll('mooringDatabase').catch(() => []),
    idbCursorAll('estimateData').catch(() => []),
    idbCursorAll('inventoryData').catch(() => []),
    idbCursorAll('vendorData').catch(() => []),
  ]);
  const items = [];
  // Right after sign-in the device copies are still filling: don't keep an index built from empty ones.
  // (Only early on: a marina with no parts at all would otherwise never keep one.)
  items.partial = performance.now() < 3 * 60 * 1000 && (!(customers || []).length || !(parts || []).length);
  const custById = new Map();
  (customers || []).forEach(c => {
    if (!c || !c.id || c.id === 'lastUpdated') return;
    custById.set(c.id, c);
    const boats = Object.values(c.Boats || {}).filter(Boolean);
    items.push({
      kind: 'customer', id: c.id, title: custSort(c),
      sub: [c.Phone, c.Email, boats.length ? `${boats.length} boat${boats.length === 1 ? '' : 's'}` : ''].filter(Boolean).join(' · '),
      hay: norm([custName(c), c.Last_Name, c.Company, c.Phone, c.Email].join(' ')), primary: norm(c.Last_Name || custName(c)),
    });
    boats.forEach(b => {
      if (!b.Boat_Name) return;
      items.push({
        kind: 'boat', id: c.id, boatId: b.Boat_ID, title: b.Boat_Name,
        sub: [[b.Boat_Year, b.Boat_Make, b.Boat_Model].filter(Boolean).join(' '), custSort(c), b.Mooring_Number].filter(Boolean).join(' · '),
        hay: norm([b.Boat_Name, b.Boat_Make, b.Boat_Model, b.Mooring_Number, b.Tag_Number].join(' ')), primary: norm(b.Boat_Name),
      });
    });
  });
  // Work orders: one entry per Work_Order, from its operations. Not filtered by status —
  // an operation's Status says nothing about whether its work order is still open (the
  // Work Orders list decides that), so the shell falls back to Closed when it isn't.
  const byWo = new Map();
  (jobs || []).forEach(j => {
    if (!j || !j.Work_Order) return;
    const w = byWo.get(j.Work_Order) || { wo: j.Work_Order, ops: 0, boat: j.Boat_Name, cust: j.Customer_Name, custId: j.Customer_ID, desc: j.Short_Description };
    w.ops++;
    w.boat = w.boat || j.Boat_Name;
    w.cust = w.cust || j.Customer_Name;
    w.desc = w.desc || j.Short_Description;
    byWo.set(j.Work_Order, w);
  });
  byWo.forEach(w => {
    const c = custById.get(w.custId);
    const cn = w.cust || (c ? custSort(c) : '');
    items.push({
      kind: 'wo', id: w.wo, title: `${/^wo/i.test(String(w.wo)) ? '' : 'WO '}${w.wo}${w.boat ? ` · ${w.boat}` : ''}`,
      sub: [cn, `${w.ops} operation${w.ops === 1 ? '' : 's'}`, w.desc].filter(Boolean).join(' · '),
      hay: norm([w.wo, w.boat, cn, w.desc].join(' ')), primary: norm(String(w.wo)),
    });
  });
  (estimates || []).forEach(e => {
    if (!e || !e.id || e.id === 'lastUpdated') return;
    const num = e.Estimate_Number || 'Draft estimate';
    const total = Number(e.Pricing?.total || 0);
    items.push({
      kind: 'estimate', id: e.id, title: `${num}${e.Boat_Name ? ` · ${e.Boat_Name}` : ''}`,
      sub: [e.Customer_Name, e.Status || 'Draft', total ? total.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '', e.Short_Description].filter(Boolean).join(' · '),
      hay: norm([e.Estimate_Number, e.Boat_Name, e.Customer_Name, e.Short_Description, 'estimate'].join(' ')), primary: norm(e.Estimate_Number || ''),
    });
  });
  (tags || []).forEach(t => {
    if (!t || !t.Tag_Number || t.Removed_Tag) return;
    const num = t.Current_Mooring_Number || '';
    items.push({
      kind: 'mooring', id: t.Tag_ID || t.id, tag: t.Tag_Number, title: num || `Tag ${t.Tag_Number}`,
      sub: [num ? `Tag ${t.Tag_Number}` : '', t.Current_Boat_Name, t.Current_Customer_Name, t.Type].filter(Boolean).join(' · '),
      hay: norm([num, t.Tag_Number, t.Current_Boat_Name, t.Current_Customer_Name].join(' ')), primary: norm(num || t.Tag_Number),
    });
  });
  (parts || []).forEach(pt => {
    if (!pt || !pt.id || pt.id === 'lastUpdated' || !(pt.Part_Number || pt.Description)) return;
    items.push({
      kind: 'part', id: pt.id, partNumber: pt.Part_Number || '', title: [pt.Part_Number, pt.Description].filter(Boolean).join(' · '),
      sub: [pt.Manufacturer, pt.Qty != null && pt.Qty !== '' ? `${pt.Qty} on hand` : '', pt.Location].filter(Boolean).join(' · '),
      hay: norm([pt.Part_Number, pt.Description, pt.Manufacturer].join(' ')), primary: norm(pt.Part_Number || pt.Description),
    });
  });
  (vendors || []).forEach(v => {
    if (!v || !v.id || v.id === 'lastUpdated' || !v.Vendor_Name) return;
    items.push({
      kind: 'vendor', id: v.id, title: v.Vendor_Name,
      sub: ['Vendor', v.Phone || v.Vendor_Phone, v.Email || v.Vendor_Email, v.Account_Number ? `Acct ${v.Account_Number}` : ''].filter(Boolean).join(' · '),
      hay: norm([v.Vendor_Name, v.Rep_Name, v.Account_Number, 'vendor supplier'].join(' ')), primary: norm(v.Vendor_Name),
    });
  });
  return items;
}

async function getIndex() {
  if (!index || Date.now() - indexAt > INDEX_TTL) {
    index = await buildIndex();
    indexAt = index.partial ? 0 : Date.now();
  }
  return index;
}

const GROUPS = [
  { kind: 'action', label: 'Create', max: 3 },
  { kind: 'page', label: 'Go to', max: 3 },
  { kind: 'wo', label: 'Work orders' },
  { kind: 'estimate', label: 'Estimates' },
  { kind: 'customer', label: 'Customers' },
  { kind: 'boat', label: 'Boats' },
  { kind: 'mooring', label: 'Moorings' },
  { kind: 'part', label: 'Parts' },
  { kind: 'vendor', label: 'Vendors', max: 3 },
  { kind: 'staff', label: 'People', max: 3 },
];
const PER_GROUP = 4;

/* People: the staff list the shell already holds (admin and office open them in Settings › Team). */
function staffItems() {
  return (hooks.listStaff?.() || []).map(u => ({
    kind: 'staff', id: u.id, title: u.name, sub: ['Staff', u.role].filter(Boolean).join(' · '),
    hay: norm(`${u.name} ${u.role || ''} staff`), primary: norm(u.name),
  }));
}

/* "On this page": the search box of the page on screen, if it has one, filled in for you. */
function pageFilterTarget() {
  const view = [...document.querySelectorAll('.page-view')].find(el => el.style.display === 'flex');
  if (!view) return null;
  let scope = view;
  const frame = view.querySelector('iframe');
  if (frame) { try { scope = frame.contentDocument?.body; } catch { return null; } }
  const input = scope && [...scope.querySelectorAll('input[type="search"]')].find(i => i.offsetParent !== null && !i.disabled);
  if (!input) return null;
  // Named after what the rail shows as open: the hub's tab, or the selected link.
  const tab = document.querySelector('#hub-bar:not([hidden]) .hub-tab[aria-selected="true"] span')?.textContent.trim();
  const link = document.querySelector('#buttonBar .page-buttons .selected-tide-button .rail-label')?.textContent.trim();
  return { input, label: tab || link || 'this page' };
}

function fillSearch(input, value, { enter = false } = {}) {
  input.focus();
  input.value = value;
  input.dispatchEvent(new Event('input', { bubbles: true }));
  input.dispatchEvent(new KeyboardEvent('keyup', { bubbles: true }));
  if (enter) input.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', bubbles: true }));
}

/** Open a page, wait for its search box (a page can still be loading), then search it. */
async function openAndSearch(open, pageId, selector, value, opts) {
  await open();
  for (let i = 0; i < 40; i++) {
    let input = null;
    try { input = document.querySelector(`#${pageId} iframe`)?.contentDocument?.querySelector(selector); } catch { return; }
    if (input && input.offsetParent !== null) { fillSearch(input, value, opts); return; }
    await new Promise(r => setTimeout(r, 200));
  }
}

/* Create: the same actions as the rail's New menu (app.js createNew). */
const ACTIONS = [
  { new: 'wo', title: 'New work order', words: 'work order wo job' },
  { new: 'estimate', title: 'New estimate', words: 'estimate quote proposal' },
  { new: 'storage', title: 'New storage contract', words: 'storage contract winter' },
  { new: 'customer', title: 'New customer', words: 'customer client owner' },
  { new: 'invoice', title: 'New invoice', words: 'invoice bill', billing: true },
  { new: 'email', title: 'New email', words: 'email mail compose send message' },
];
function actionItems() {
  return ACTIONS.filter(a => !a.billing || hooks.canInvoice?.()).map(a => ({
    kind: 'action', new: a.new, title: a.title, sub: '',
    hay: norm(`new create add ${a.title} ${a.words}`), primary: norm(a.title),
  }));
}

/* Go to: every page the rail opens (a hub's pages by name too), plus a few that live elsewhere. */
const labelOf = el => [...el.querySelectorAll('.rail-label')].map(l => l.textContent.trim()).join(' ') || el.getAttribute('aria-label') || '';
const usable = el => el && !el.classList.contains('feature-off');
function pageItems() {
  const out = [];
  document.querySelectorAll('#buttonBar .rail-item').forEach(el => {
    if (!el.id || !usable(el) || isHub(el.id)) return;
    const hub = hubOf(el.id);
    if (hub && !usable(document.getElementById(hub))) return;
    const tab = memberTab(el.id);
    const title = labelOf(el);
    if (!title) return;
    out.push({
      kind: 'page', go: el.id, title, sub: hub ? `${HUBS[hub].label} › ${tab.label}` : 'Page',
      hay: norm([title, tab?.label, hub && HUBS[hub].label, tab?.desc].join(' ')), primary: norm(title),
    });
  });
  const extra = [
    { title: 'Estimates', sub: 'Work Orders › Estimates', run: () => hooks.openEstimates?.(), words: 'quotes list' },
    ...(hooks.canInvoice?.() ? [{ title: 'Invoices', sub: 'Work Orders › Invoices', run: () => hooks.openInvoices?.(), words: 'payments statements accounts' }] : []),
    { title: 'Settings', sub: 'Your menu', go: 'settings-button', words: 'preferences setup' },
    { title: 'Help & how-to', sub: 'Your menu', go: 'help-open-button', words: 'guide manual' },
  ];
  extra.forEach(x => out.push({ kind: 'page', go: x.go, run: x.run, title: x.title, sub: x.sub, hay: norm(`${x.title} ${x.words}`), primary: norm(x.title) }));
  return out;
}

/* Recent: the last records picked here (this device). */
const RECENT_KEY = 'railSearchRecent:v1';
const RECENT_MAX = 5;
const recentRead = () => { try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]') || []; } catch { return []; } };
function recentAdd(it) {
  if (!['wo', 'estimate', 'customer', 'boat', 'mooring', 'part', 'vendor'].includes(it.kind)) return;
  const keep = { kind: it.kind, id: it.id, boatId: it.boatId, tag: it.tag, partNumber: it.partNumber, title: it.title, sub: it.sub };
  const list = [keep, ...recentRead().filter(r => !(r.kind === it.kind && r.id === it.id && r.boatId === it.boatId))].slice(0, RECENT_MAX);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(list)); } catch { /* storage blocked */ }
}

function rank(item, words) {
  if (!words.every(w => item.hay.includes(w))) return -1;
  const q = words.join(' ');
  if (item.primary === q) return 0;
  if (item.primary.startsWith(q)) return 1;
  if (item.hay.split(/\s+/).some(t => t.startsWith(words[0]))) return 2;
  return 3;
}

let results = [];
let active = -1;

const canStart = it => it.kind === 'customer' || it.kind === 'boat';
function rowHTML(it, i) {
  return `<div class="rs-row" role="option" id="rs-opt-${i}" data-i="${i}">
        <span class="rs-kind rs-kind-${it.kind}" aria-hidden="true"></span>
        <span class="rs-main"><span class="rs-title">${escapeHTML(it.title)}</span>${it.sub ? `<span class="rs-sub">${escapeHTML(it.sub)}</span>` : ''}</span>
        ${canStart(it) ? `<span class="rs-acts"><button type="button" class="rs-act" data-start="wo" tabindex="-1" title="New work order for ${escapeHTML(it.title)} (Shift+Enter)">+ WO</button><button type="button" class="rs-act" data-start="estimate" tabindex="-1" title="New estimate for ${escapeHTML(it.title)}">+ Est</button></span>` : ''}
      </div>`;
}

/** The box is empty: quick create, then what you opened lately. */
function showStart() {
  const box = document.getElementById('railSearchResults');
  const recent = recentRead();
  const quick = actionItems().filter(a => ['wo', 'estimate', 'customer'].includes(a.new));
  results = [];
  let html = '<div class="rs-group">Create</div>';
  quick.forEach(it => { html += rowHTML(it, results.push(it) - 1); });
  if (recent.length) {
    html += '<div class="rs-group">Recent</div>';
    recent.forEach(it => { html += rowHTML(it, results.push(it) - 1); });
  }
  html += '<div class="rs-hint">Type a name, boat, WO or EST number, mooring, part, page, or <b>new</b> to create. On a customer or boat, <b>Shift+Enter</b> starts a work order for them. Press <b>?</b> for all shortcuts.</div>';
  box.innerHTML = html;
  active = -1;
  paintActive();
  openResults();
}

async function runSearch(q) {
  const box = document.getElementById('railSearchResults');
  // Commas split words too: "Abernathy, Colleen" (as the lists show names) finds her.
  const words = norm(q).trim().split(/[\s,]+/).filter(Boolean);
  if (!words.length) { showStart(); return; }
  const items = [...actionItems(), ...pageItems(), ...staffItems(), ...(await getIndex())];
  const input = document.getElementById('railSearchInput');
  if (norm(input.value).trim() !== norm(q).trim()) return; // typed on since
  const scored = items.map(it => ({ it, r: rank(it, words) })).filter(x => x.r >= 0)
    .sort((a, b) => a.r - b.r || a.it.title.localeCompare(b.it.title, undefined, { numeric: true }));
  results = [];
  let html = '';
  GROUPS.forEach(g => {
    const rows = scored.filter(x => x.it.kind === g.kind);
    if (!rows.length) return;
    const max = g.max || PER_GROUP;
    html += `<div class="rs-group">${escapeHTML(g.label)}${rows.length > max ? `<span>${rows.length}</span>` : ''}</div>`;
    rows.slice(0, max).forEach(({ it }) => { html += rowHTML(it, results.push(it) - 1); });
  });
  // Last, so Enter still opens the best match: search the page on screen, and the invoices.
  const typed = q.trim();
  const more = [];
  const target = typed.length > 1 && pageFilterTarget();
  if (target) more.push({ kind: 'filter', title: `Filter ${target.label} for “${typed}”`, sub: 'The list on screen', q: typed });
  if (typed.length > 1 && hooks.canInvoice?.()) more.push({ kind: 'invoices', title: `Search invoices for “${typed}”`, sub: 'Opens Invoices, searched', q: typed });
  // Nothing matched: say so, and still offer the page's filter and the invoices under it.
  if (!results.length) {
    html = `<div class="rs-empty"><b>No match for “${escapeHTML(typed)}”</b>Searches work order and estimate numbers, customer and boat names, phone, email, mooring and tag numbers, parts, vendors, people and page names. Type <b>new</b> to create something.</div>`;
  }
  if (more.length) {
    html += '<div class="rs-group">More</div>';
    more.forEach(it => { html += rowHTML(it, results.push(it) - 1); });
  }
  box.innerHTML = html;
  active = results.length ? 0 : -1;
  paintActive();
  openResults();
}

function paintActive() {
  const box = document.getElementById('railSearchResults');
  box.querySelectorAll('.rs-row').forEach(r => r.classList.toggle('active', Number(r.dataset.i) === active));
  const input = document.getElementById('railSearchInput');
  if (active >= 0) {
    input.setAttribute('aria-activedescendant', `rs-opt-${active}`);
    box.querySelector(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  } else input.removeAttribute('aria-activedescendant');
}

function openResults() {
  const box = document.getElementById('railSearchResults');
  const wrap = document.getElementById('railSearch').getBoundingClientRect();
  if (window.innerWidth > 600) {
    box.style.left = `${Math.round(wrap.right + 12)}px`;
    box.style.top = `${Math.round(wrap.top)}px`;
  } else {
    box.style.left = '';
    box.style.top = `${Math.round(wrap.bottom + 8)}px`;
  }
  box.hidden = false;
  document.getElementById('railSearchInput').setAttribute('aria-expanded', 'true');
}

function closeResults() {
  const box = document.getElementById('railSearchResults');
  if (!box) return;
  box.hidden = true;
  document.getElementById('railSearchInput')?.setAttribute('aria-expanded', 'false');
}

/** start: 'wo' | 'estimate' — make one for this customer or boat instead of opening it. */
async function choose(i, { start = null } = {}) {
  const it = results[i];
  if (!it) return;
  const input = document.getElementById('railSearchInput');
  const target = it.kind === 'filter' ? pageFilterTarget() : null; // before the box closes
  closeResults();
  input.value = '';
  input.blur();
  closeMobileRail();
  if (start && canStart(it)) { await hooks.createNew?.(start, { customerId: it.id, boatId: it.boatId }); return; }
  recentAdd(it);
  try {
    if (it.kind === 'filter') { if (target) fillSearch(target.input, it.q); }
    else if (it.kind === 'invoices') await hooks.openInvoices?.(it.q);
    else if (it.kind === 'part') await openAndSearch(() => document.getElementById('inventory-menu-open-button')?.click(), 'inventoryDatabase', '#inv-search', it.partNumber || it.title, { enter: true });
    else if (it.kind === 'vendor') await openAndSearch(() => hooks.openSettings?.('vendors'), 'settings', '#vdSearch', it.title);
    else if (it.kind === 'staff') await openAndSearch(() => hooks.openSettings?.('team'), 'settings', '#rosterSearch', it.title);
    else if (it.kind === 'action') await hooks.createNew?.(it.new);
    else if (it.kind === 'page') { if (it.run) await it.run(); else document.getElementById(it.go)?.click(); }
    else if (it.kind === 'estimate') await hooks.openEstimate?.(it.id);
    else if (it.kind === 'wo') await hooks.openWorkOrder?.(it.id);
    else if (it.kind === 'customer' || it.kind === 'boat') await hooks.openCustomer?.(it.id, it.boatId);
    else if (it.kind === 'mooring') await hooks.openMooring?.(it.tag, it.title);
  } catch (err) {
    console.error('[shellNav] open failed', err);
  }
}

function wireSearch() {
  const input = document.getElementById('railSearchInput');
  const box = document.getElementById('railSearchResults');
  if (!input || !box) return;
  let t = null;
  input.addEventListener('focus', () => {
    getIndex().catch(() => {});
    runSearch(input.value);
    // Estimates reach the device only when asked for; once they have, search again with them.
    hooks.syncEstimates?.().then((fresh) => {
      if (!fresh) return;
      indexAt = 0;
      if (document.activeElement === input && input.value.trim()) runSearch(input.value);
    }).catch(() => {});
  });
  input.addEventListener('input', () => { clearTimeout(t); t = setTimeout(() => runSearch(input.value), 90); });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowDown' && results.length) { e.preventDefault(); active = (active + 1) % results.length; paintActive(); }
    else if (e.key === 'ArrowUp' && results.length) { e.preventDefault(); active = (active - 1 + results.length) % results.length; paintActive(); }
    else if (e.key === 'Enter' && active >= 0 && !box.hidden) { e.preventDefault(); choose(active, { start: e.shiftKey ? 'wo' : null }); }
    else if (e.key === 'Escape') { closeResults(); input.value = ''; input.blur(); }
  });
  box.addEventListener('mousedown', (e) => e.preventDefault()); // keep focus in the input
  box.addEventListener('click', (e) => {
    const r = e.target.closest('.rs-row');
    if (r) choose(Number(r.dataset.i), { start: e.target.closest('[data-start]')?.dataset.start || null });
  });
  box.addEventListener('mousemove', (e) => { const r = e.target.closest('.rs-row'); if (r && Number(r.dataset.i) !== active) { active = Number(r.dataset.i); paintActive(); } });
  input.addEventListener('blur', () => setTimeout(() => { if (document.activeElement !== input) closeResults(); }, 120));
  window.addEventListener('resize', () => { if (!box.hidden) openResults(); });

  // Ctrl+K (⌘K) from anywhere jumps to search, too. In the Pinned style it opens All pages (railStyles.js).
  document.addEventListener('keydown', (e) => {
    if (!(e.ctrlKey || e.metaKey) || e.key.toLowerCase() !== 'k' || e.altKey) return;
    if (document.body.classList.contains('rail-style-pinned')) return;
    if (input.offsetParent === null) return;
    e.preventDefault();
    input.focus();
    input.select();
  });
  // "/" from anywhere in the shell (not while typing) jumps to search.
  document.addEventListener('keydown', (e) => {
    if (e.key !== '/' || e.ctrlKey || e.metaKey || e.altKey) return;
    const a = document.activeElement;
    if (a && (a.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(a.tagName))) return;
    if (input.offsetParent === null) return; // rail hidden (mobile, closed)
    e.preventDefault();
    input.focus();
    input.select();
  });
}

/* ============ INIT ============ */

/**
 * hooks: { openWorkOrder(wo), openCustomer(customerId, boatId), openMooring(tagNumber, label), openEstimate(id),
 *   openEstimates(), openInvoices(search), createNew(kind, { customerId, boatId }), canInvoice(), listStaff(),
 *   openSettings(tab), syncEstimates() }
 * — the shell owns page switching; this module only decides what was picked.
 */
export function initShellNav(h = {}) {
  hooks = h;
  wireCounts();
  wireAvatar();
  wireSearch();
  mountSyncChip(document.getElementById('railSync'), { theme: 'light', place: 'right' });
  wireUserMenu();
  wireUserSync();
  // The cached index goes stale when the app syncs new records; drop it on any sync.
  window.addEventListener('focus', () => { indexAt = 0; });
}
